import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderForm = ({ preselected }) => {
  const [form, setForm] = useState({ name: "", email: "", items: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preselected) setForm((f) => ({ ...f, items: preselected }));
  }, [preselected]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/orders`, form);
      toast.success("Order sent! Els will stitch up a reply soon.");
      setForm({ name: "", email: "", items: "", message: "" });
    } catch (err) {
      toast.error("Oh no, a loose thread! Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "rounded-xl bg-white border-[#D3D7CF] h-12 px-4 text-[#2C402B] placeholder:text-[#576956]/50 focus-visible:ring-2 focus-visible:ring-[#8AA387] focus-visible:ring-offset-0";

  return (
    <section className="py-28 sm:py-36" data-testid="order-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl mx-auto bg-[#E8EAE3] rounded-[2.5rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(44,64,43,0.08)] border border-[#D3D7CF]"
        >
          <p className="font-hand text-2xl text-[#C27F67] -rotate-2 mb-2">let's make it yours</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl text-[#2C402B]">
            Place an order
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#576956] leading-relaxed">
            Tell Els what you'd love and she'll follow up to confirm colors,
            details and pickup or delivery.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="order-form">
            <div>
              <label htmlFor="order-name" className="block text-sm font-semibold text-[#2C402B] mb-2">Your name</label>
              <Input
                id="order-name"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="e.g. Els's future best customer"
                className={inputCls}
                data-testid="order-name-input"
              />
            </div>
            <div>
              <label htmlFor="order-email" className="block text-sm font-semibold text-[#2C402B] mb-2">Email</label>
              <Input
                id="order-email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className={inputCls}
                data-testid="order-email-input"
              />
            </div>
            <div>
              <label htmlFor="order-items" className="block text-sm font-semibold text-[#2C402B] mb-2">What would you like?</label>
              <select
                id="order-items"
                required
                value={form.items}
                onChange={update("items")}
                className={`${inputCls} w-full appearance-none cursor-pointer`}
                data-testid="order-items-select"
              >
                <option value="" disabled>Pick a treasure…</option>
                <option value="Scrunchie ($5)">Scrunchie — $5</option>
                <option value="Bandana ($10)">Bandana — $10</option>
                <option value="Ear Warmer ($10)">Ear Warmer — $10 (custom colors!)</option>
                <option value="Scrunchie + Bandana ($15)">Scrunchie + Bandana — $15</option>
                <option value="Something custom">Something custom</option>
              </select>
            </div>
            <div>
              <label htmlFor="order-message" className="block text-sm font-semibold text-[#2C402B] mb-2">Message</label>
              <Textarea
                id="order-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Favorite colors, quantities, special requests…"
                className={`${inputCls} h-auto min-h-[110px] py-3`}
                data-testid="order-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="order-submit-button"
              className="w-full rounded-full bg-[#2C402B] text-[#F9F6F0] py-4 font-heading font-medium transition-[transform,background-color,box-shadow,opacity] duration-300 hover:bg-[#435E42] hover:scale-[1.02] hover:shadow-[0_14px_30px_rgba(44,64,43,0.25)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send my order"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default OrderForm;
